import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { MessageSquare, Settings, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps, ContentBlock } from '@/types/flow';
import BlocksPreview from './BlocksPreview';

const MessageNode = ({ data, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const message = config.message || '';
    const buttons = data.buttons || [];

    const truncateMessage = (text: string, maxLength: number = 80) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-[#2196F3] shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-3.5 h-3.5 text-[#2196F3]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Mensagem'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Messenger</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5">
                {message ? (
                    <div className="bg-[#f0f4f8] rounded-lg rounded-tl-sm px-3 py-2 text-[12px] text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                        {truncateMessage(message, 100)}
                    </div>
                ) : (
                    <div className="text-center py-3">
                        <p className="text-xs text-gray-400">Mensagem não configurada</p>
                    </div>
                )}

                {buttons.length > 0 && (
                    <div className="mt-2 space-y-1">
                        {buttons.slice(0, 2).map((button) => (
                            <div
                                key={button.id}
                                className="text-[11px] bg-blue-50 border border-blue-100 rounded-md px-2.5 py-1.5 text-[#2196F3] truncate text-center font-medium"
                            >
                                {button.button_text}
                            </div>
                        ))}
                        {buttons.length > 2 && (
                            <span className="text-[10px] text-gray-400 block text-center">+{buttons.length - 2} botões</span>
                        )}
                    </div>
                )}
            </div>

            <BlocksPreview blocks={(config.blocks || []) as ContentBlock[]} />

            {/* Actions */}
            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <Handle type="target" position={Position.Left} id="target" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            <Handle type="source" position={Position.Right} id="source" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
    );
};

export default memo(MessageNode);
